import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Key as KeyIcon,
  Group as GroupIcon,
  MoreVert as MoreVertIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import { DataTableProps } from '../types';

const DataTable: React.FC<DataTableProps> = ({
  data,
  columns,
  onAdd,
  onEdit,
  onDelete,
  onToggleStatus,
  onResetPassword,
  onChangeRole,
  onEditTenants,
  onManagePermissions,
  onEditModules,
  title,
  searchFields = [],
  pagination,
  searchTerm: externalSearchTerm,
  onSearchChange,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  isRowDisabled,
  disabledRowTooltip,
}) => {
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  // Use external search term if provided (server-side mode), otherwise use internal state (client-side mode)
  const [internalSearchTerm, setInternalSearchTerm] = useState<string>('');
  const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
  const [orderBy, setOrderBy] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  
  // Ref to maintain focus on search input
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Effect to maintain focus after data changes (when using server-side search)
  useEffect(() => {
    if (pagination && searchInputRef.current && searchTerm) {
      // Small delay to ensure DOM has updated after search results
      const timeoutId = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [data, pagination, searchTerm]);

  // Use external pagination if provided, otherwise use internal state
  const currentPage = pagination ? pagination.page - 1 : page; // Convert to 0-based index
  const currentPageSize = pagination ? pagination.pageSize : rowsPerPage;
  const totalItems = pagination ? pagination.totalItems : data.length;

  // Debug: Log when DataTable receives new data
  React.useEffect(() => {
    console.log('📊 DataTable received new data:', {
      dataLength: data.length,
      currentPage: currentPage + 1,
      currentPageSize,
      totalItems,
      hasPagination: !!pagination
    });
  }, [data, currentPage, currentPageSize, totalItems, pagination]);

  const handleRequestSort = (property: string): void => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    
    // If using server-side pagination, notify parent to reload data with new sort order
    if (pagination && pagination.onSortChange) {
      pagination.onSortChange(property, newOrder);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number): void => {
    if (pagination) {
      pagination.onPageChange(newPage + 1); // Convert back to 1-based index
    } else {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newPageSize = parseInt(event.target.value, 10);
    if (pagination) {
      pagination.onPageSizeChange(newPageSize);
    } else {
      setRowsPerPage(newPageSize);
      setPage(0);
    }
  };

  const filteredData = useMemo(() => {
    console.log('🔍 Filtering data:', { 
      originalDataLength: data.length, 
      searchTerm, 
      orderBy, 
      order,
      hasPagination: !!pagination
    });
    
    // If using server-side pagination, skip client-side filtering
    if (pagination) {
      console.log('🔍 Server-side mode: skipping client-side filtering');
      return data;
    }
    
    // Client-side filtering for non-paginated mode
    let filtered = data;
    
    if (searchTerm) {
      filtered = data.filter(item =>
        searchFields.some(field => {
          const value = item[field];
          if (value === null || value === undefined) return false;
          return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
      console.log('🔍 After search filter:', { filteredLength: filtered.length });
    }

    if (orderBy) {
      filtered.sort((a, b) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        if (typeof aValue === 'string') {
          return order === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        if (aValue instanceof Date) {
          return order === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
        }
        
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      });
    }

    console.log('🔍 Final filtered data:', { 
      filteredLength: filtered.length,
      firstItem: filtered[0] ? { id: filtered[0].id, name: filtered[0].name } : null
    });
    
    return filtered;
  }, [data, searchTerm, orderBy, order, searchFields, pagination]);

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'enabled':
        return 'success';
      case 'inactive':
      case 'disabled':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (!onSelectionChange) return;
    
    if (event.target.checked) {
      const allIds = filteredData.map(row => row.id).filter(Boolean);
      onSelectionChange(allIds);
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectRow = (id: string): void => {
    if (!onSelectionChange) return;
    
    const selectedIndex = selectedRows.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedRows, id];
    } else {
      newSelected = selectedRows.filter(selectedId => selectedId !== id);
    }

    onSelectionChange(newSelected);
  };

  const isSelected = (id: string): boolean => selectedRows.indexOf(id) !== -1;
  const isAllSelected = filteredData.length > 0 && filteredData.every(row => selectedRows.includes(row.id));
  const isSomeSelected = selectedRows.length > 0 && !isAllSelected;

  const ActionMenu: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    
    // Check if the row should have disabled actions using the injected function
    const isRowDisabledValue = isRowDisabled ? isRowDisabled(row) : false;

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    // Helper function to return MenuItem (tooltips removed - now shown on action button)
    const wrapMenuItem = (menuItem: React.ReactElement) => {
      return menuItem;
    };

    const menuItems: React.ReactNode[] = [];

    if (onEdit) {
      menuItems.push(
        wrapMenuItem(
          <MenuItem 
            key="edit" 
            onClick={() => { if (!isRowDisabledValue) { onEdit(row); handleClose(); } }}
            disabled={isRowDisabledValue}
            sx={{ '&:hover': { backgroundColor: '#e3f2fd' } }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" sx={{ color: '#2196f3' }} />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )
      );
    }

    if (onToggleStatus) {
      const isActive = row.status?.toLowerCase() === 'active' || row.status?.toLowerCase() === 'enabled';
      menuItems.push(
        wrapMenuItem(
          <MenuItem 
            key="toggle" 
            onClick={() => { if (!isRowDisabledValue) { onToggleStatus(row); handleClose(); } }}
            disabled={isRowDisabledValue}
            sx={{ 
              '&:hover': { 
                backgroundColor: isActive ? '#ffebee' : '#e8f5e8' 
              } 
            }}
          >
            <ListItemIcon>
              {isActive ? (
                <BlockIcon fontSize="small" sx={{ color: '#f44336' }} />
              ) : (
                <CheckCircleIcon fontSize="small" sx={{ color: '#4caf50' }} />
              )}
            </ListItemIcon>
            <ListItemText>{isActive ? 'Disable' : 'Enable'}</ListItemText>
          </MenuItem>
        )
      );
    }

    if (onResetPassword) {
      menuItems.push(
        wrapMenuItem(
          <MenuItem 
            key="password" 
            onClick={() => { if (!isRowDisabledValue) { onResetPassword(row); handleClose(); } }}
            disabled={isRowDisabledValue}
            sx={{ '&:hover': { backgroundColor: '#fff3e0' } }}
          >
            <ListItemIcon>
              <KeyIcon fontSize="small" sx={{ color: '#ff9800' }} />
            </ListItemIcon>
            <ListItemText>Reset Password</ListItemText>
          </MenuItem>
        )
      );
    }

    if (onChangeRole) {
      menuItems.push(
        wrapMenuItem(
          <MenuItem 
            key="role" 
            onClick={() => { if (!isRowDisabledValue) { onChangeRole(row); handleClose(); } }}
            disabled={isRowDisabledValue}
            sx={{ '&:hover': { backgroundColor: '#e3f2fd' } }}
          >
            <ListItemIcon>
              <GroupIcon fontSize="small" sx={{ color: '#2196f3' }} />
            </ListItemIcon>
            <ListItemText>Change Role</ListItemText>
          </MenuItem>
        )
      );
    }

    if (onEditTenants) {
      menuItems.push(
        wrapMenuItem(
          <MenuItem 
            key="tenants" 
            onClick={() => { if (!isRowDisabledValue) { onEditTenants(row); handleClose(); } }}
            disabled={isRowDisabledValue}
            sx={{ '&:hover': { backgroundColor: '#f3e5f5' } }}
          >
            <ListItemIcon>
              <GroupIcon fontSize="small" sx={{ color: '#9c27b0' }} />
            </ListItemIcon>
            <ListItemText>Edit Tenants</ListItemText>
          </MenuItem>
        )
      );
    }

    if (onManagePermissions) {
      menuItems.push(
        wrapMenuItem(
          <MenuItem 
            key="permissions" 
            onClick={() => { if (!isRowDisabledValue) { onManagePermissions(row); handleClose(); } }}
            disabled={isRowDisabledValue}
            sx={{ '&:hover': { backgroundColor: '#fff3e0' } }}
          >
            <ListItemIcon>
              <KeyIcon fontSize="small" sx={{ color: '#ff9800' }} />
            </ListItemIcon>
            <ListItemText>Manage Permissions</ListItemText>
          </MenuItem>
        )
      );
    }

    if (onEditModules) {
      menuItems.push(
        wrapMenuItem(
          <MenuItem 
            key="modules" 
            onClick={() => { if (!isRowDisabledValue) { onEditModules(row); handleClose(); } }}
            disabled={isRowDisabledValue}
            sx={{ '&:hover': { backgroundColor: '#e8f5e9' } }}
          >
            <ListItemIcon>
              <ExtensionIcon fontSize="small" sx={{ color: '#4caf50' }} />
            </ListItemIcon>
            <ListItemText>Edit Modules</ListItemText>
          </MenuItem>
        )
      );
    }

    if (onDelete) {
      menuItems.push(
        wrapMenuItem(
          <MenuItem 
            key="delete" 
            onClick={() => { if (!isRowDisabledValue) { onDelete(row); handleClose(); } }}
            disabled={isRowDisabledValue}
            sx={{ '&:hover': { backgroundColor: '#ffebee' } }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: '#f44336' }} />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )
      );
    }

    if (menuItems.length === 0) return null;

    // Disable the action button if the row is disabled and all menu items are disabled
    const allItemsDisabled = isRowDisabledValue && menuItems.length > 0;

    return (
      <>
        <Tooltip 
          title={isRowDisabledValue && disabledRowTooltip ? disabledRowTooltip : ''}
          arrow
          placement="top"
        >
          <span>
            <IconButton
              size="small"
              onClick={handleClick}
              aria-controls={open ? 'action-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              disabled={allItemsDisabled}
            >
              <MoreVertIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Menu
          id="action-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'action-button',
          }}
        >
          {menuItems}
        </Menu>
      </>
    );
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'visible' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{title}</Typography>
        {onAdd && (
          <Tooltip title="Add New">
            <IconButton onClick={onAdd} color="primary" sx={{ backgroundColor: 'primary.main', color: 'white', '&:hover': { backgroundColor: 'primary.dark' } }}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      
      {searchFields.length > 0 && (
        <Box sx={{ p: 2, pb: 0 }}>
          <TextField
            ref={searchInputRef}
            fullWidth
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            autoFocus={pagination ? true : false}
            onChange={(e) => {
              const newValue = e.target.value;
              if (onSearchChange) {
                // Server-side mode: notify parent
                onSearchChange(newValue);
              } else {
                // Client-side mode: update internal state
                setInternalSearchTerm(newValue);
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
          />
        </Box>
      )}
      
      <TableContainer sx={{ width: '100%' }}>
        <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isSomeSelected}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    inputProps={{ 'aria-label': 'select all users' }}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.numeric ? 'right' : 'left'}
                  sortDirection={orderBy === column.id ? order : false}
                  sx={{ 
                    minWidth: column.minWidth,
                    width: column.width || 'auto'
                  }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {(onEdit || onDelete || onToggleStatus || onResetPassword || onChangeRole || onEditTenants || onManagePermissions || onEditModules) && (
                <TableCell align="center" sx={{ width: '80px' }}>Actions</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData
              .slice(pagination ? 0 : page * rowsPerPage, pagination ? filteredData.length : page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                const isRowSelected = isSelected(row.id);
                return (
                <TableRow 
                  hover 
                  key={row.id || index}
                  selected={isRowSelected}
                  onClick={selectable ? () => handleSelectRow(row.id) : undefined}
                  sx={selectable ? { cursor: 'pointer' } : {}}
                >
                  {selectable && (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isRowSelected}
                        onChange={() => handleSelectRow(row.id)}
                        inputProps={{ 'aria-labelledby': `enhanced-table-checkbox-${index}` }}
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.numeric ? 'right' : 'left'}>
                        {column.render ? column.render(value, row) : (
                          column.id === 'status' ? (
                            <Chip 
                              label={value} 
                              color={getStatusColor(value)} 
                              size="small" 
                            />
                          ) : (
                            value
                          )
                        )}
                      </TableCell>
                    );
                  })}
                  {(onEdit || onDelete || onToggleStatus || onResetPassword || onChangeRole || onEditTenants || onManagePermissions || onEditModules) && (
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <ActionMenu row={row} />
                    </TableCell>
                  )}
                </TableRow>
              );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalItems}
        rowsPerPage={currentPageSize}
        page={currentPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Rows per page:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
        }
      />
    </Paper>
  );
};

export default DataTable; 